/**
 * Splitting a translated sentence around the one value inside it that someone else wrote.
 *
 * WHY THIS EXISTS. A blind critic, reading our Overview against Chatwoot Captain without
 * knowing whose was whose: *"'No assistant on فرع السالمية — واتساب' runs RTL text inline
 * in an LTR heading … Give the Arabic rows their own direction."*
 *
 * `dir="auto"` on the sentence is necessary and not sufficient. It picks a base direction
 * for the WHOLE string from its first strong character — so an English frame around an
 * Arabic name correctly stays LTR — but the Arabic run inside is still resolved by the
 * bidi algorithm as part of one paragraph. Anything neutral that touches it (the em dash
 * in `فرع السالمية — واتساب`, the quote marks around a source title, a trailing full
 * stop) gets absorbed into the Arabic run and moves. The name renders with its
 * punctuation rearranged, which is not a styling nitpick: it is the shop's own name shown
 * back to them wrong.
 *
 * The fix is `<bdi>` — a real bidi ISOLATE, so the value is laid out as one opaque
 * neutral atom inside the sentence and cannot exchange punctuation with it. That needs
 * the value in its own ELEMENT, which means the sentence cannot arrive as one
 * already-interpolated string. Hence this: interpolate the sentence with `MARKER` where
 * the value goes, hand the result here, and render the three parts it returns.
 *
 *   isolateParts(t('…TITLE', { name: MARKER }), 'فرع السالمية — واتساب')
 *     → { before: 'No assistant on ', value: 'فرع السالمية — واتساب', after: '' }
 *
 * WHY A MARKER RATHER THAN THE RAW TEMPLATE. `t(key)` with no parameters does not hand
 * back `'No assistant on {name}'`; vue-i18n interpolates regardless and substitutes an
 * empty string, so splitting on `{name}` silently produced a row with no name in it (this
 * cost one test run to find and is exactly the kind of thing a spec should own). Going
 * through the real interpolation also means locale-specific plural and number formatting
 * still applies to everything else in the sentence.
 *
 * `MARKER` is U+0000, which no copy in any locale contains and which is split back out
 * before anything reaches the DOM.
 *
 * Placeholders are ASCII-braced and identical in every locale, so this works unchanged on
 * the Arabic table (`لا يوجد مساعد على {name}`), where the frame is RTL and the isolated
 * value is the English one.
 */

/** Substituted for the value, then split back out. Never rendered. */
export const MARKER = '\u0000';

/**
 * @param {string} rendered A translated sentence containing `MARKER` where the value goes.
 * @param {string} value The value to isolate.
 * @returns {{before: string, value: string, after: string}}
 */
export const isolateParts = (rendered, value) => {
  const text = typeof rendered === 'string' ? rendered : '';
  const at = text.indexOf(MARKER);
  // A translation that lost its placeholder still renders as a whole sentence rather
  // than as a blank row — the frame is the part an owner can act on, and dropping it
  // because the value has nowhere to sit would be the worse failure.
  if (at === -1) return { before: text, value: '', after: '' };
  return {
    before: text.slice(0, at),
    value: value == null ? '' : String(value),
    after: text.slice(at + MARKER.length),
  };
};

/** The whole line is the value: user content with no frame around it. */
export const wholeValue = value => ({
  before: '',
  value: value == null ? '' : String(value),
  after: '',
});

/**
 * "Somebody wrote Arabic in this line" — ARABIC LETTERS ONLY, and the word letters is the
 * whole point. W-13.
 *
 * The obvious test is the Arabic block, `/[؀-ۿ]/` (U+0600–U+06FF), and it is wrong for
 * this question, because that block opens with SIGNS, PUNCTUATION AND DIGITS: `؟`
 * (U+061F), `،` (U+060C), `٠`–`٩` (U+0660–U+0669) and the tatweel all live in it. A
 * Kuwaiti typing an English sentence on an Arabic keyboard produces "Are you open
 * today؟" — an entirely Latin sentence that the block test calls Arabic. That is not a
 * hypothetical: customer messages arrive that way every day, and the Test it screen's
 * Arabic guarantee reads a real customer message now, not copy we authored.
 *
 * So the ranges here are letters: U+0620–U+063F and U+0641–U+064A, U+066E–U+06D3, and
 * the Arabic Supplement U+0750–U+077F. Diacritics and the Arabic-Indic digits are
 * deliberately NOT enough on their own — they decorate or count, they do not say a
 * language was written.
 *
 * THE GAP AT U+0640 IS THE TATWEEL, carved out of an otherwise continuous run for the
 * same reason the punctuation is: it is the kashida, a stretching glyph that pads a word
 * to a width. It carries no sound and belongs to no language — "Helloـo" would
 * otherwise satisfy an Arabic-LETTER test, which is exactly the class of wrong this
 * function exists to stop. Arabic written by an actual person always brings real letters
 * with it, so nothing legitimate is lost by refusing to count the padding.
 *
 * ONE Arabic word inside a Latin sentence DOES count, and should: that customer wrote
 * Arabic. This answers "is any of it Arabic", never "is this line Arabic".
 */
const ARABIC_LETTER = /[ؠ-ؿف-يٮ-ۓݐ-ݿ]/;

export const hasArabicLetter = text =>
  typeof text === 'string' && ARABIC_LETTER.test(text);

export default isolateParts;
